import { ArrowLeft, CheckCircle2, CircleAlert, Info } from "lucide-react"
import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"

import { navigateTo } from "@/app/app-router"
import { ROUTE_PATHS } from "@/app/site-metadata"
import { PageContainer } from "@/components/shared/page-container"
import { PrivacyNotice } from "@/components/shared/privacy-notice"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { AnalysisProgress } from "@/features/analysis/analysis-progress"
import {
  clearMarginAnalysis,
  clearNormalizedInputs,
  marginAnalysisService,
  runMarginAnalysis,
} from "@/features/analysis"
import type {
  MarginAnalysisFailure,
  MarginAnalysisSuccess,
} from "@/features/analysis/margin-analysis-types"
import { useAuthState } from "@/features/auth/auth-context"
import { reportExportService } from "@/features/exports/report-export-service"
import { FileInspectionPanel } from "@/features/file-inspection/file-inspection-panel"
import { fileInspectionService } from "@/features/file-inspection/file-inspection-service"
import { useFileInspection } from "@/features/file-inspection/use-file-inspection"
import { FilePicker } from "@/features/file-selection/file-picker"
import { ResultsPage } from "@/features/results/results-page"
import { manualOverrideService } from "@/features/results/manual-override-service"
import {
  analysisSetupReducer,
  createDefaultAnalysisSetupDraft,
  validateAnalysisConfiguration,
} from "@/features/setup/analysis-configuration"
import { ColumnMappingSection } from "@/features/setup/column-mapping-section"
import { EngineReadiness } from "@/features/setup/engine-readiness"
import { MarginSettingsSection } from "@/features/setup/margin-settings-section"
import { SetupReadinessSummary } from "@/features/setup/setup-readiness-summary"
import { duckDBEngine } from "@/lib/duckdb"

function SetupShell() {
  const supplier = useFileInspection("supplier")
  const catalog = useFileInspection("catalog")
  const { status: authStatus, requestSignIn, signInWillReloadPage } = useAuthState()
  const analysisSnapshot = useSyncExternalStore(
    marginAnalysisService.subscribe,
    marginAnalysisService.getSnapshot,
    marginAnalysisService.getSnapshot,
  )
  const [setupDraft, dispatch] = useReducer(
    analysisSetupReducer,
    undefined,
    createDefaultAnalysisSetupDraft,
  )
  const [isExecuting, setIsExecuting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [wasCancelled, setWasCancelled] = useState(false)
  const [analysisError, setAnalysisError] = useState<
    MarginAnalysisFailure["error"] | null
  >(null)
  const [completedAnalysis, setCompletedAnalysis] =
    useState<MarginAnalysisSuccess | null>(null)
  const workflowGeneration = useRef(0)
  const cancellationInProgress = useRef(false)
  const lastResolvedAuthStatus = useRef<"anonymous" | "authenticated" | null>(null)

  const leaveWorkflow = async () => {
    await fileInspectionService.releaseAll()
    await duckDBEngine.dispose()
    navigateTo(ROUTE_PATHS.landing)
    window.scrollTo({ top: 0, behavior: "auto" })
  }

  const supplierResult =
    supplier.state.status === "ready" || supplier.state.status === "warning"
      ? supplier.state.result
      : null
  const catalogResult =
    catalog.state.status === "ready" || catalog.state.status === "warning"
      ? catalog.state.result
      : null
  const bothFilesReady = Boolean(supplierResult && catalogResult)
  const supplierColumns = useMemo(
    () => supplierResult?.columns.map((column) => column.name) ?? [],
    [supplierResult],
  )
  const catalogColumns = useMemo(
    () => catalogResult?.columns.map((column) => column.name) ?? [],
    [catalogResult],
  )

  useEffect(() => {
    if (!supplierResult) return
    dispatch({
      type: "supplier-inspected",
      suggestions: supplierResult.suggestions,
    })
  }, [supplierResult])

  useEffect(() => {
    if (!catalogResult) return
    dispatch({
      type: "catalog-inspected",
      suggestions: catalogResult.suggestions,
    })
  }, [catalogResult])

  useEffect(() => {
    if (authStatus === "loading") return

    const previous = lastResolvedAuthStatus.current
    lastResolvedAuthStatus.current = authStatus
    if (previous !== "authenticated" || authStatus !== "anonymous") return

    // A report still being prepared belongs to the signed-in state and must not download.
    reportExportService.cancelActive()
    const generation = workflowGeneration.current
    void (async () => {
      const cleared = await manualOverrideService
        .cancelPendingAndClear()
        .catch(() => null)
      if (!cleared) {
        workflowGeneration.current += 1
        setCompletedAnalysis(null)
        await clearMarginAnalysis().catch(() => undefined)
        return
      }
      if (generation !== workflowGeneration.current) return

      setCompletedAnalysis((current) =>
        current ? { ...current, metadata: cleared.metadata } : null,
      )
    })()
  }, [authStatus])

  const validation = useMemo(
    () =>
      validateAnalysisConfiguration(setupDraft, {
        supplier: { ready: Boolean(supplierResult), columns: supplierColumns },
        catalog: { ready: Boolean(catalogResult), columns: catalogColumns },
      }),
    [catalogColumns, catalogResult, setupDraft, supplierColumns, supplierResult],
  )

  const chooseSupplierFile = (file: File) => {
    setWasCancelled(false)
    dispatch({ type: "supplier-file-changed" })
    void supplier.chooseFile(file)
  }

  const chooseCatalogFile = (file: File) => {
    setWasCancelled(false)
    dispatch({ type: "catalog-file-changed" })
    void catalog.chooseFile(file)
  }

  const analyzeCatalog = async () => {
    if (!validation.isReady || !validation.configuration || isExecuting) return

    setAnalysisError(null)
    setWasCancelled(false)
    setCompletedAnalysis(null)
    setIsExecuting(true)
    const generation = ++workflowGeneration.current

    try {
      const result = await runMarginAnalysis(validation.configuration)
      // A cancelled or superseded run reports the aborted engine as a failure; it is neither
      // an error to show nor a result to keep.
      if (generation !== workflowGeneration.current) return
      if (result.status === "ERROR") {
        setAnalysisError(result.error)
        return
      }

      setCompletedAnalysis(result)
      window.scrollTo({ top: 0, behavior: "auto" })
    } catch {
      if (generation !== workflowGeneration.current) return
      setAnalysisError({
        code: "ANALYSIS_FAILED",
        userMessage:
          "We couldn't prepare the analysis results. Your files are still on this computer. Review your setup and try again.",
      })
      await clearMarginAnalysis().catch(() => undefined)
    } finally {
      // A cancellation keeps the progress screen until the engine is usable again; every
      // other outcome, including a run superseded by sign-out, leaves it here.
      if (!cancellationInProgress.current) setIsExecuting(false)
    }
  }

  const cancelAnalysis = async () => {
    if (!isExecuting || isCancelling) return
    workflowGeneration.current += 1
    cancellationInProgress.current = true
    setIsCancelling(true)

    try {
      // Terminating the worker is the reliable way to stop a running statement. The fresh
      // engine has no partial relations, and the inspected files are registered again so
      // the user can analyze without choosing them a second time.
      await duckDBEngine.restart()
      await fileInspectionService.restoreRegisteredInputs()
      setWasCancelled(true)
    } catch {
      setAnalysisError({
        code: "ANALYSIS_FAILED",
        userMessage:
          "The analysis was cancelled, but the local analysis engine could not restart. Reload the page to continue.",
      })
    } finally {
      cancellationInProgress.current = false
      setIsExecuting(false)
      setIsCancelling(false)
      window.scrollTo({ top: 0, behavior: "auto" })
    }
  }

  const startNewScan = async () => {
    workflowGeneration.current += 1
    reportExportService.cancelActive()
    setCompletedAnalysis(null)
    setAnalysisError(null)
    setWasCancelled(false)
    dispatch({ type: "reset" })
    await manualOverrideService.cancelPendingAndClear().catch(() => null)
    await Promise.all([supplier.clearFile(), catalog.clearFile()])
    await Promise.all([clearMarginAnalysis(), clearNormalizedInputs()])
    window.scrollTo({ top: 0, behavior: "auto" })
  }

  if (completedAnalysis && validation.configuration) {
    return (
      <ResultsPage
        result={completedAnalysis}
        currency={validation.configuration.options.currency}
        numberFormat={validation.configuration.options.numberFormat}
        onStartNewScan={startNewScan}
        onMetadataChanged={(metadata) =>
          setCompletedAnalysis((current) =>
            current ? { ...current, metadata } : current,
          )
        }
      />
    )
  }

  if (isExecuting) {
    return (
      <AnalysisProgress
        stage={
          analysisSnapshot.state === "running"
            ? analysisSnapshot.stage
            : "preparing-results"
        }
        onCancel={() => void cancelAnalysis()}
        isCancelling={isCancelling}
      />
    )
  }

  return (
    <main id="main-content" className="min-h-[calc(100svh-4rem)] py-10 sm:py-12">
      <PageContainer width="app">
        <button
          type="button"
          onClick={() => void leaveWorkflow()}
          className="inline-flex min-h-10 items-center gap-2 rounded-md text-sm font-medium text-text-secondary outline-none hover:text-text-primary focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back to overview
        </button>

        <div className="mt-6 max-w-copy">
          <h1 className="text-[28px] leading-9 font-semibold tracking-[-0.02em] text-text-primary">
            Check your catalog
          </h1>
          <p className="mt-3 text-base leading-[26px] text-text-secondary">
            Compare supplier costs with current selling prices in a guided, browser-local
            workflow.
          </p>
          <PrivacyNotice className="mt-4" />
        </div>

        {signInWillReloadPage && (
          <Alert variant="info" className="mt-6" data-testid="sign-in-first-notice">
            <Info aria-hidden="true" />
            <AlertTitle>Planning to sign in for full results? Sign in first.</AlertTitle>
            <AlertDescription>
              In this browser, signing in reloads the page, which clears an analysis in
              progress. Sign in now, then choose your files. Anonymous analysis works
              without signing in. Your files stay on your computer either way.
              <div className="mt-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={requestSignIn}
                >
                  Sign in free
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <section
          className="mt-8 rounded-lg border border-border bg-surface p-6 sm:p-8"
          aria-labelledby="setup-step-heading"
        >
          <div className="flex items-start gap-4">
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[13px] font-semibold text-brand"
              aria-hidden="true"
            >
              1
            </span>
            <div>
              <h2
                id="setup-step-heading"
                className="text-lg leading-7 font-semibold text-text-primary"
              >
                Choose your files
              </h2>
              <p className="mt-1 text-sm leading-[22px] text-text-secondary">
                Select your supplier cost file and current catalog. Each file is inspected
                locally before the next column-mapping step.
              </p>
            </div>
          </div>

          <div className="mt-6 border-t border-border pt-5">
            <EngineReadiness />
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <FilePicker
              role="supplier"
              title="Supplier file"
              prompt="Choose Supplier File"
              state={supplier.state}
              onChoose={chooseSupplierFile}
            />
            <FilePicker
              role="catalog"
              title="Current catalog"
              prompt="Choose Catalog File"
              state={catalog.state}
              onChoose={chooseCatalogFile}
            />
          </div>

          {bothFilesReady && (
            <div
              className="mt-6 flex items-start gap-3 border-t border-border pt-5 text-[13px] leading-[18px] text-text-secondary"
              role="status"
            >
              <CheckCircle2 className="size-4 shrink-0 text-ok" aria-hidden="true" />
              <p>
                <span className="font-semibold text-text-primary">
                  Both files are ready.
                </span>{" "}
                Confirm the suggested mappings and configure your margin rules below.
              </p>
            </div>
          )}
        </section>

        {supplierResult && catalogResult && (
          <>
            <ColumnMappingSection
              draft={setupDraft}
              validation={validation}
              supplierColumns={supplierColumns}
              catalogColumns={catalogColumns}
              supplierSuggestions={supplierResult.suggestions}
              catalogSuggestions={catalogResult.suggestions}
              dispatch={dispatch}
            />
            <MarginSettingsSection
              draft={setupDraft}
              validation={validation}
              dispatch={dispatch}
            />
            <SetupReadinessSummary
              validation={validation}
              onAnalyze={() => void analyzeCatalog()}
            />
            {wasCancelled && !analysisError && (
              <Alert className="mt-5" role="status" data-testid="analysis-cancelled">
                <CheckCircle2 aria-hidden="true" />
                <AlertTitle>Analysis cancelled.</AlertTitle>
                <AlertDescription>
                  No results were kept. Your files and settings are still here, so you can
                  adjust them or analyze again.
                </AlertDescription>
              </Alert>
            )}
            {analysisError && (
              <Alert variant="destructive" className="mt-5">
                <CircleAlert aria-hidden="true" />
                <AlertTitle>We couldn't complete this analysis.</AlertTitle>
                <AlertDescription>
                  {analysisError.userMessage} Your files are still on this computer.
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        {(supplierResult || catalogResult) && (
          <section className="mt-8" aria-labelledby="file-review-heading">
            <div className="mb-4">
              <h2
                id="file-review-heading"
                className="text-[15px] font-semibold text-text-primary"
              >
                Review inspected files
              </h2>
              <p className="mt-1 text-xs leading-[18px] text-text-muted">
                Use these compact previews to verify source columns and sample values.
              </p>
            </div>
            <div className="space-y-5">
              {supplierResult && (
                <FileInspectionPanel role="supplier" result={supplierResult} />
              )}
              {catalogResult && (
                <FileInspectionPanel role="catalog" result={catalogResult} />
              )}
            </div>
          </section>
        )}

        <p className="mt-5 text-xs leading-[18px] text-text-muted md:hidden">
          For large catalogs, we recommend using Catalog Margin Guard on a desktop
          computer.
        </p>
      </PageContainer>
    </main>
  )
}

export { SetupShell }
